import 'swiper/scss';
import { FreeMode } from 'swiper';
import { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSearchParams } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Entry } from 'contentful';

import { client } from '../../helpers/contentful';
import { Spinner, ToolCard, SkeletonPage, SearchBar, SeoIntro } from '../../components';
import { IBrushFields } from '../../@types/generated/contentful';
import { analytics } from '../../helpers/analytics';

const Models = () => {
  const [brushes, setBrushes] = useState<Entry<IBrushFields>[]>([]);
  const [newBrushes, setNewBrushes] = useState<Entry<IBrushFields>[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm = searchParams.get('search')?.trim() || '';
  const isSearchLanding = Boolean(searchTerm);

  const getModelsQuery = (skip: number) => {
    const query: Record<string, string | number | boolean> = {
      skip,
      limit: 12,
      'fields.categoryName': '3D Models',
      content_type: 'brush',
    };

    if (searchTerm) {
      query.query = searchTerm;
    }

    return query;
  };

  const trackModelsLoaded = (loadedCount: number, totalCount: number, skip: number) => {
    analytics.track("Content Collection Loaded", {
      category: "3D Models",
      source: searchTerm ? "models_search" : "models_all",
      query: searchTerm || undefined,
      loaded_count: loadedCount,
      total_count: totalCount,
      skip,
    });
  };

  useEffect(() => {
    const getNewBrushes = async () => {
      const response = await client
        .getEntries<IBrushFields>({
          'fields.categoryName': '3D Models',
          content_type: 'brush',
          'fields.featuredText[exists]': true,
        })
        .then((r) => r.items);

      return response;
    };

    getNewBrushes().then((r) => setNewBrushes(r));
  }, []);

  const getBrushes = async () => {
    const response = await client
      .getEntries<IBrushFields>(getModelsQuery(brushes.length))
      .then((r) => r);

    if (total === 0) {
      setTotal(response.total);
    }

    setBrushes((state) => [...state, ...response.items]);
    trackModelsLoaded(response.items.length, response.total, brushes.length);
    return response.items;
  };

  useEffect(() => {
    let isCurrent = true;

    setLoading(true);
    setBrushes([]);
    setTotal(0);

    client
      .getEntries<IBrushFields>(getModelsQuery(0))
      .then((response) => {
        if (!isCurrent) {
          return;
        }

        setTotal(response.total);
        setBrushes(response.items);
        trackModelsLoaded(response.items.length, response.total, 0);
      })
      .finally(() => {
        if (isCurrent) {
          setLoading(false);
        }
      });

    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleSearch = (value: string) => {
    const nextParams = new URLSearchParams(searchParams);

    if (value) {
      nextParams.set('search', value);
      analytics.track("Search Performed", {
        query: value,
        category: "3D Models",
        source: "models",
      });
    } else {
      nextParams.delete('search');
    }

    nextParams.delete('tool');
    setSearchParams(nextParams);
  };

  return (
    <div className='home'>
      {!isLoading || brushes.length > 0 ? (
        <>
          {!isSearchLanding && (
            <SeoIntro
              eyebrow='3D reference assets'
              title='3D models for Procreate reference and illustration'
              description='Use 3D reference models to plan anatomy, poses, lighting, perspective, characters, and composition directly inside your Procreate drawing process.'
            />
          )}
          {!isSearchLanding && newBrushes.length > 0 && (
            <div className='border-t-2'>
              <div className='overflow-hidden px-6 py-8 xs:px-4 xs:py-6'>
                <div className='max-w-[1240px] mx-auto w-full'>
                  <div className='mx-auto pb-4'>
                    <h2 className='text-lg leading-xl font-bold'>For you</h2>
                  </div>
                  <Swiper
                    modules={[FreeMode]}
                    slidesPerView='auto'
                    spaceBetween={12}
                    centerInsufficientSlides
                    breakpoints={{ 480: { spaceBetween: 20 } }}
                    className='overflow-visible'
                  >
                    {newBrushes.map(({ fields, sys }) => (
                      <SwiperSlide className='w-[220px] sm:w-[180px] xs:w-[170px]' key={sys.id}>
                        <ToolCard size='small' set={fields} isBrushset category='Brushes' isFeatured />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </div>
            </div>
          )}
          <div className='border-t-2 w-full'>
            <div className={`mx-auto max-w-[1240px] w-full px-6 xs:px-4 ${isSearchLanding ? 'py-4 xs:py-3' : 'py-6 xs:py-4'}`}>
              <div className='mb-4 flex items-center justify-between gap-4 md:flex-col md:items-stretch'>
                <h2 className='shrink-0 text-lg leading-xl font-bold'>
                  {searchTerm ? `3D models for "${searchTerm}"` : 'All models'}
                </h2>
                <div className='w-full max-w-[420px] md:max-w-none'>
                  <SearchBar
                    value={searchTerm}
                    placeholder='Search 3D models'
                    onSearch={handleSearch}
                  />
                </div>
              </div>
              {brushes.length > 0 ? (
                <InfiniteScroll
                  dataLength={brushes.length}
                  next={getBrushes}
                  // eslint-disable-next-line react/jsx-boolean-value
                  hasMore={brushes.length < total}
                  scrollableTarget='app'
                  style={{ overflow: 'visible' }}
                  loader={
                    <div className='mt-4 flex justify-center'>
                      <Spinner theme='light' />
                    </div>
                  }
                >
                  <ul className='grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-5 xs:grid-cols-2 xs:gap-3 xxs:grid-cols-1'>
                    {brushes.map(({ fields, sys }) => (
                      <li key={sys.id}>
                        <ToolCard size='large' set={fields} category='3D Models' />
                      </li>
                    ))}
                  </ul>
                </InfiniteScroll>
              ) : (
                <div className='rounded-lg bg-main-grey-200 px-4 py-8 text-center text-sm leading-sm text-main-grey-800'>
                  No 3D models found.
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <SkeletonPage showSwiper={!isSearchLanding} title='models' />
      )}
    </div>
  );
};

export { Models };
